import React, { useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { MessageSquare, Send, Loader2, Check, AlertCircle } from "lucide-react";
import { submitFeedback, FeedbackSubmission } from "../services/feedbackService";

interface FeedbackWidgetProps {
  /** Whether the widget should be visible as a floating button */
  showFloatingButton?: boolean;
}

export const FeedbackWidget: React.FC<FeedbackWidgetProps> = ({
  showFloatingButton = true,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FeedbackSubmission>({
    feedbackType: "general",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.subject.trim() || !formData.message.trim()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitSuccess(false);
    setSubmitError(null);

    try {
      const result = await submitFeedback(formData);
      
      if (result.success) {
        setSubmitSuccess(true);
        // Reset form
        setFormData({
          feedbackType: "general",
          subject: "",
          message: "",
        });
        
        // Close dialog after 2 seconds
        setTimeout(() => {
          setIsOpen(false);
          setSubmitSuccess(false);
        }, 2000);
      } else {
        setSubmitError(result.error || "Unknown error");
      }
    } catch (error: any) {
      console.error("Error submitting feedback:", error);
      setSubmitError(error.message || "Unknown error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      // Reset form when closing
      setFormData({
        feedbackType: "general",
        subject: "",
        message: "",
      });
      setSubmitSuccess(false);
      setSubmitError(null);
    }
  };

  return (
    <>
      {showFloatingButton && (
        <Button
          onClick={() => setIsOpen(true)}
          variant="gradient"
          size="lg"
          className="fixed bottom-6 right-6 z-50 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-all"
          aria-label="Submit Feedback"
        >
          <MessageSquare className="h-6 w-6" />
        </Button>
      )}

      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Share Your Feedback
            </DialogTitle>
            <DialogDescription>
              We'd love to hear your thoughts! Your feedback helps us improve the app.
            </DialogDescription>
          </DialogHeader>

          {submitError && (
            <div className="flex items-center gap-3 p-4 rounded-lg border bg-destructive/10 border-destructive/20">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0" />
              <p className="text-sm text-destructive">Failed to submit feedback: {submitError}</p>
            </div>
          )}

          {submitSuccess ? (
            <div className="flex flex-col items-center justify-center py-8 space-y-4">
              <div className="h-16 w-16 rounded-full bg-success/20 flex items-center justify-center">
                <Check className="h-8 w-8 text-success" />
              </div>
              <div className="text-center space-y-2">
                <h3 className="text-lg font-semibold">Thank you!</h3>
                <p className="text-muted-foreground">
                  Your feedback has been submitted successfully. We'll review it soon.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="feedback-type">Feedback Type</Label>
                <Select
                  value={formData.feedbackType}
                  onValueChange={(value: "bug" | "feature" | "general" | "other") =>
                    setFormData((prev) => ({ ...prev, feedbackType: value }))
                  }
                >
                  <SelectTrigger id="feedback-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bug">🐛 Bug Report</SelectItem>
                    <SelectItem value="feature">💡 Feature Request</SelectItem>
                    <SelectItem value="general">💬 General Feedback</SelectItem>
                    <SelectItem value="other">📝 Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="feedback-subject">Subject</Label>
                <Input
                  id="feedback-subject"
                  value={formData.subject}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, subject: e.target.value }))
                  }
                  placeholder="Brief summary of your feedback"
                  required
                  maxLength={200}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="feedback-message">Message</Label>
                <Textarea
                  id="feedback-message"
                  value={formData.message}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, message: e.target.value }))
                  }
                  placeholder="Tell us more about your feedback, suggestions, or issues..."
                  required
                  rows={6}
                  maxLength={2000}
                />
                <p className="text-xs text-muted-foreground">
                  {formData.message.length}/2000 characters
                </p>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsOpen(false)}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="gradient"
                  disabled={isSubmitting || !formData.subject.trim() || !formData.message.trim()}
                  className="gap-2"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      Submit Feedback
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};





